import { useViewContext } from "@editor-providers";
import { LinkPopUp } from "@library/components/LinkPopUp";
import { getTabByAnchorInside } from "@library/shared/utils/getTabByAnchorInside";
import { useState, type MouseEvent } from "react";
import type { DefaultButtonProps, GlobalProps } from "../types";
import { Container } from "./Container";

// Function to decode HTML-encoded URLs
const decodeUrl = (url: string): string => {
  if (!url) return url;

  // Use browser's built-in HTML entity decoding
  const textarea = document.createElement("textarea");
  textarea.innerHTML = url;
  const htmlDecoded = textarea.value;

  // Then decode URI components if needed
  try {
    return decodeURIComponent(htmlDecoded);
  } catch {
    // If decodeURIComponent fails, return the HTML-decoded version
    return htmlDecoded;
  }
};

export const Element = ({
  settings,
  isSecondary,
}: {
  settings: DefaultButtonProps & GlobalProps;
  isSecondary: boolean;
}) => {
  const [showPopUp, setShowPopUp] = useState(false);
  const { view } = useViewContext();
  const isPdf = view === "pdf";

  const isAnchor =
    (settings.anchorTrigger && !settings.selectedLinkType) ||
    settings.selectedLinkType === "anchor";

  const isLink =
    (!settings.anchorTrigger && !settings.selectedLinkType) ||
    settings.selectedLinkType !== "anchor";

  const handleOnClick = (e: MouseEvent) => {
    if (isPdf) return;
    e.preventDefault();
    setShowPopUp(true);
  };

  const handleAnchorClick = (e: MouseEvent) => {
    if (!settings.anchorTrigger) return;
    e.preventDefault();

    const anchorTargetElement = document.querySelector(
      '[data-anchor-target="' + settings.anchorTrigger + '"]',
    );

    if (anchorTargetElement) {
      const tab = getTabByAnchorInside(anchorTargetElement);
      if (tab) {
        tab.click();
        setTimeout(() => {
          const tabsCombo = tab.closest('[data-combo-name="Tabs"]');
          (tabsCombo instanceof HTMLElement
            ? tabsCombo
            : anchorTargetElement
          ).scrollIntoView();
        });
        return;
      }

      const timelineItem = anchorTargetElement.closest(".timeline-item");
      if (timelineItem instanceof HTMLElement) {
        const timelineCombo = timelineItem.closest(
          '.timeline-combo[data-timeline-type="horizontal"]',
        );
        if (timelineCombo instanceof HTMLElement) {
          timelineCombo.scrollIntoView();
          timelineItem.click();
        }
        return;
      }

      anchorTargetElement.scrollIntoView();
    }
  };

  const renderLink = (
    href: string,
    onClick?: (event: React.MouseEvent) => void,
  ) => {
    const decodedHref = decodeUrl(href);
    const onClickHandler = (e: MouseEvent) => {
      if (!decodedHref || decodedHref === "#") return e.preventDefault();
      if (onClick) onClick(e);
    };
    return (
      <a
        className="buttonWrapper !tw-mb-0 tw-inline-flex tw-w-auto tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-px-4 tw-py-[14px] tw-text-center tw-font-paragraph !tw-no-underline [line-height:initial] hover:!tw-no-underline"
        href={decodedHref}
        target={"_blank"}
        rel={"noreferrer"}
        onClick={onClickHandler}
        dangerouslySetInnerHTML={{ __html: settings.title }}
      />
    );
  };

  return (
    <Container settings={settings} isSecondary={isSecondary}>
      {isLink &&
        renderLink(
          settings.url,
          settings.openInPopup ? handleOnClick : undefined,
        )}
      {isAnchor &&
        (isPdf ? (
          renderLink(
            window.location.href.split("?")[0] +
              "#" +
              encodeURIComponent(settings.anchorTrigger),
          )
        ) : (
          <p
            className="buttonWrapper !tw-mb-0 tw-inline-flex tw-w-auto tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-px-4 tw-py-[14px] tw-text-center tw-font-paragraph !tw-no-underline [line-height:initial] hover:!tw-no-underline"
            onClick={handleAnchorClick}
            dangerouslySetInnerHTML={{ __html: settings.title }}
          />
        ))}

      {showPopUp && (
        <LinkPopUp
          src={decodeUrl(settings.url)}
          closePopUp={() => setShowPopUp(false)}
        />
      )}
    </Container>
  );
};
